import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartOptions, ChartType, ChartDataSets } from "chart.js";
import { BaseChartDirective } from "ng2-charts";

import { McKinseyChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";
import { ZoneComponent } from "@/shared-module/components";

/** Internal working copy of a data point while positions are being de-overlapped. */
interface PlacedPoint {
  seriesIndex: number;
  pointIndex: number;
  yIndex: number;
  x: number;
  realX: number;
  y: number;
  xRawLabel: string;
}

/**
 * McKinsey Matrix — port of renderMcKinseyChart in
 * Faculty\Faculty.UI\Scripts\mega-charts.js. The geometry (3x3 zones, the Y window
 * computation and the de-overlap spread) is kept identical so that students and
 * faculty read the same picture.
 */
@Component({
  selector: "app-mckinseychart",
  templateUrl: "mckinseychart.component.html",
})
export class McKinseyChartComponent implements ZoneComponent, OnInit {
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService,
  ) {}

  @Input() data: McKinseyChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @Input() isZoom: boolean;

  @ViewChild("template", { static: true }) template;
  @ViewChild(BaseChartDirective) baseChart: BaseChartDirective;

  public chartType: ChartType = "scatter";
  public chartLegend = true;
  public chartData: ChartDataSets[] = [];
  public chartPlugins = [];
  public chartOptions: ChartOptions = {};

  /** Actual EBITDA/Revenue ratios, one per product row, ascending. */
  private productRatios: number[] = [];
  /** Where each row is actually drawn - see build(). */
  private productDisplayY: number[] = [];
  private productLabels: string[] = [];
  private productMeasureLabels: string[] = [];
  private zoneLabels: string[] = ["Weak", "Medium", "Strong"];

  private readonly xZone1 = 100 / 3;
  private readonly xZone2 = 200 / 3;
  private yZone1 = 0;
  private yZone2 = 0;

  private readonly zoneColors = [
    ["rgba(231,76,60,0.07)", "rgba(231,76,60,0.07)", "rgba(241,196,15,0.07)"],
    ["rgba(231,76,60,0.07)", "rgba(241,196,15,0.07)", "rgba(46,213,115,0.07)"],
    ["rgba(241,196,15,0.07)", "rgba(46,213,115,0.07)", "rgba(46,213,115,0.07)"],
  ];

  get iconSize(): number {
    return this.isZoom ? 100 : 80;
  }

  get chartHeight(): number {
    return this.isZoom ? 820 : 600;
  }

  /** Rough size of the rendered plot area, used to translate the icon size into axis units. */
  get plotWidth(): number {
    return this.isZoom ? 1100 : 900;
  }

  get plotHeight(): number {
    return this.chartHeight - 60;
  }

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);
    this.build();
  }

  /**
   * Zone components are re-created whenever their data changes (ZoneContainerComponent
   * assigns the inputs imperatively), so building once from ngOnInit also covers the
   * period switch in the zoom dialog.
   */
  private build() {
    if (!this.data || !this.data.series) {
      return;
    }

    this.productLabels = this.data.productLabels || [];
    this.productMeasureLabels = this.data.productMeasureLabels || [];
    if (this.data.zoneLabels && this.data.zoneLabels.length === 3) {
      this.zoneLabels = this.data.zoneLabels;
    }

    // Every series carries the same yValue per product slot — read them from the first one.
    this.productRatios =
      this.data.series.length > 0
        ? this.data.series[0].points.map((pt) => pt.yValue)
        : [];

    const bounds = this.computeYBounds();

    // Rows are drawn at their true EBITDA/Revenue ratio, so two business units with close
    // ratios print their line, their labels and every team icon on top of each other.
    // productDisplayY keeps the ratio order and stays as close as possible to the real value,
    // but guarantees one icon of vertical room between two rows. The true ratio stays visible
    // in the axis label, which the server formats into productLabels.
    const unitPerPx = (bounds.yMax - bounds.yMin) / this.plotHeight;
    const halfIcon = (this.iconSize / 2) * unitPerPx;
    this.productDisplayY = this.spreadRows(
      this.productRatios,
      (this.iconSize + 8) * unitPerPx,
      bounds.yMin + halfIcon,
      bounds.yMax - halfIcon,
    );

    const yRange = bounds.yMax - bounds.yMin;
    this.yZone1 = bounds.yMin + yRange / 3;
    this.yZone2 = bounds.yMin + (2 * yRange) / 3;

    this.chartPlugins = [this.buildZonePlugin()];
    this.chartOptions = this.buildOptions(bounds.yMin, bounds.yMax);
    this.chartData = this.buildDatasets();

    this.loadIcons();
  }

  /**
   * Y axis bounds:
   *   1. start with the guaranteed window [-15%, +30%];
   *   2. expand it if any data value falls outside;
   *   3. squeeze it so the highest value sits in the top third and the lowest in the bottom third;
   *   4. pad it so no point lands exactly on a boundary.
   */
  private computeYBounds(): { yMin: number; yMax: number } {
    const windowMin = -0.15;
    const windowMax = 0.3;
    const padding = 0.02;

    const minRatio =
      this.productRatios.length > 0 ? this.productRatios[0] : windowMin;
    const maxRatio =
      this.productRatios.length > 0
        ? this.productRatios[this.productRatios.length - 1]
        : windowMax;

    let yMin = Math.min(windowMin, minRatio);
    let yMax = Math.max(windowMax, maxRatio);

    if (maxRatio < yMin + (2 * (yMax - yMin)) / 3) {
      yMax = maxRatio + (yMax - yMin) / 6;
    }
    if (minRatio > yMin + (yMax - yMin) / 3) {
      yMin = minRatio - (yMax - yMin) / 6;
    }

    return { yMin: yMin - padding, yMax: yMax + padding };
  }

  /**
   * Pushes ascending values apart until consecutive ones are at least minGap from each other,
   * keeping them inside [lo, hi] and moving them as little as possible.
   */
  private spreadRows(
    values: number[],
    minGap: number,
    lo: number,
    hi: number,
  ): number[] {
    const n = values.length;
    if (n === 0 || hi <= lo) {
      return values.slice();
    }

    const gap = n > 1 ? Math.min(minGap, (hi - lo) / (n - 1)) : 0;
    const out = values.slice();

    // Push up from the bottom, pull down from the top, then push up once more so the gap
    // still holds where the two passes fought over the available room.
    out[0] = Math.max(out[0], lo);
    for (let i = 1; i < n; i++) {
      out[i] = Math.max(out[i], out[i - 1] + gap);
    }
    out[n - 1] = Math.min(out[n - 1], hi);
    for (let i = n - 2; i >= 0; i--) {
      out[i] = Math.min(out[i], out[i + 1] - gap);
    }
    out[0] = Math.max(out[0], lo);
    for (let i = 1; i < n; i++) {
      out[i] = Math.max(out[i], out[i - 1] + gap);
    }

    // The passes above push everything away from the bottom, which would drag a tight group
    // upwards as a whole. Slide it back onto its own centre of gravity, as far as the
    // remaining room allows, so the spread is shared instead of biased one way.
    const mean = (a: number[]) => a.reduce((sum, v) => sum + v, 0) / a.length;
    let shift = mean(values) - mean(out);
    shift =
      shift > 0
        ? Math.min(shift, hi - out[n - 1])
        : Math.max(shift, lo - out[0]);
    if (shift !== 0) {
      for (let i = 0; i < n; i++) {
        out[i] += shift;
      }
    }
    return out;
  }

  private getXZoneIndex(x: number): number {
    if (x < this.xZone1) {
      return 0;
    }
    if (x < this.xZone2) {
      return 1;
    }
    return 2;
  }

  private getXZoneBounds(x: number): { min: number; max: number } {
    if (x < this.xZone1) {
      return { min: 0, max: this.xZone1 };
    }
    if (x < this.xZone2) {
      return { min: this.xZone1, max: this.xZone2 };
    }
    return { min: this.xZone2, max: 100 };
  }

  /** Nudges teams that share a row and an X zone apart so their markers stay readable. */
  private spreadClosePoints(points: PlacedPoint[]) {
    // The x axis spans 110 units (-5..105) across the plot, so one icon is this many units
    // wide - teams closer than that overlap.
    const iconXUnits = (this.iconSize / this.plotWidth) * 110;
    const zonePadding = 0.8;

    for (let rowIndex = 0; rowIndex < this.productLabels.length; rowIndex++) {
      const rowPoints = points
        .filter((pt) => pt.yIndex === rowIndex)
        .sort((a, b) => a.realX - b.realX);

      // One pass per x zone, so a team is never nudged out of its own column and a team
      // that is moved cannot land on a neighbour that stayed put.
      for (let zoneIndex = 0; zoneIndex < 3; zoneIndex++) {
        const zonePoints = rowPoints.filter(
          (pt) => this.getXZoneIndex(pt.realX) === zoneIndex,
        );
        if (zonePoints.length < 2) {
          continue;
        }

        const bounds = this.getXZoneBounds(zonePoints[0].realX);
        const spread = this.spreadRows(
          zonePoints.map((pt) => pt.realX),
          iconXUnits,
          bounds.min + zonePadding,
          bounds.max - zonePadding,
        );

        zonePoints.forEach((pt, j) => (pt.x = spread[j]));
      }
    }
  }

  private buildDatasets(): ChartDataSets[] {
    const allPoints: PlacedPoint[] = [];
    this.data.series.forEach((serie, seriesIndex) => {
      serie.points.forEach((point, pointIndex) => {
        allPoints.push({
          seriesIndex,
          pointIndex,
          yIndex: point.yIndex,
          x: point.xValue,
          realX: point.xValue,
          y: this.productDisplayY[point.yIndex],
          xRawLabel: point.xRawLabel,
        });
      });
    });
    this.spreadClosePoints(allPoints);

    return this.data.series.map((serie, seriesIndex) => {
      const points = serie.points.map((point, pointIndex) => {
        const placed = allPoints.find(
          (pt) => pt.seriesIndex === seriesIndex && pt.pointIndex === pointIndex,
        );
        return {
          x: placed.x,
          y: placed.y,
          realX: placed.realX,
          xRawLabel: placed.xRawLabel,
        };
      });

      return {
        label: serie.teamName,
        data: points,
        backgroundColor: serie.color,
        borderColor: serie.color,
        // Kept hidden until the marker icons are loaded, otherwise Chart.js briefly
        // paints oversized default circles.
        pointRadius: 0,
        pointHoverRadius: this.iconSize / 2 + 4,
      } as ChartDataSets;
    });
  }

  /** Loads the team marker svgs into offscreen canvases used as Chart.js pointStyle. */
  private loadIcons() {
    const colorNames = this.data.series.map((s) =>
      (s.colorName || "blue").toLowerCase(),
    );
    const uniqueColors = colorNames.filter((v, i, a) => a.indexOf(v) === i);
    const loadedIcons: { [colorName: string]: HTMLCanvasElement } = {};
    let remaining = uniqueColors.length;

    const applyIcons = () => {
      this.chartData.forEach((dataset, index) => {
        dataset.pointStyle = loadedIcons[colorNames[index]];
        dataset.pointRadius = this.iconSize / 2;
      });
      if (this.baseChart && this.baseChart.chart) {
        this.baseChart.chart.update();
      }
    };

    const makeIcon = (colorName: string, img: HTMLImageElement | null) => {
      const offscreen = document.createElement("canvas");
      offscreen.width = this.iconSize;
      offscreen.height = this.iconSize;
      const context = offscreen.getContext("2d");
      if (img) {
        context.drawImage(img, 0, 0, this.iconSize, this.iconSize);
      } else {
        context.beginPath();
        context.arc(
          this.iconSize / 2,
          this.iconSize / 2,
          this.iconSize / 2 - 1,
          0,
          Math.PI * 2,
        );
        context.fillStyle = "rgba(100,100,200,0.8)";
        context.fill();
      }
      loadedIcons[colorName] = offscreen;
      remaining--;
      if (remaining === 0) {
        applyIcons();
      }
    };

    if (remaining === 0) {
      return;
    }

    uniqueColors.forEach((colorName) => {
      const img = new Image();
      img.onload = () => makeIcon(colorName, img);
      img.onerror = () => makeIcon(colorName, null);
      img.src = `img/charts/${colorName}.svg`;
    });
  }

  /** Paints the 3x3 background zones, the product rows and the Weak/Medium/Strong banners. */
  private buildZonePlugin() {
    const self = this;

    return {
      beforeDatasetsDraw(chart: any) {
        const ctx = chart.ctx;
        let xScale = null;
        let yScale = null;
        Object.keys(chart.scales).forEach((key) => {
          const scale = chart.scales[key];
          if (scale.isHorizontal()) {
            xScale = scale;
          } else {
            yScale = scale;
          }
        });
        if (!xScale || !yScale) {
          return;
        }
        const area = chart.chartArea;

        const xPx = [
          area.left,
          xScale.getPixelForValue(self.xZone1),
          xScale.getPixelForValue(self.xZone2),
          area.right,
        ];
        const yPx = [
          area.bottom,
          yScale.getPixelForValue(self.yZone1),
          yScale.getPixelForValue(self.yZone2),
          area.top,
        ];

        ctx.save();

        for (let xi = 0; xi < 3; xi++) {
          for (let yi = 0; yi < 3; yi++) {
            ctx.fillStyle = self.zoneColors[xi][yi];
            ctx.fillRect(
              xPx[xi],
              yPx[yi + 1],
              xPx[xi + 1] - xPx[xi],
              yPx[yi] - yPx[yi + 1],
            );
          }
        }

        // Thin line at each product row (drawn at productDisplayY, not at the raw ratio).
        ctx.strokeStyle = "rgba(180,180,180,0.4)";
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        self.productDisplayY.forEach((rowY) => {
          const py = yScale.getPixelForValue(rowY);
          ctx.beginPath();
          ctx.moveTo(area.left, py);
          ctx.lineTo(area.right, py);
          ctx.stroke();
        });

        // Measure label on each product row, just above its line.
        ctx.font = "italic 10px sans-serif";
        ctx.textBaseline = "bottom";
        ctx.textAlign = "left";
        self.productDisplayY.forEach((rowY, index) => {
          const measureLabel = self.productMeasureLabels[index] || "";
          if (!measureLabel) {
            return;
          }
          const py = yScale.getPixelForValue(rowY);
          ctx.fillStyle = "rgba(80,80,80,0.75)";
          ctx.fillText(measureLabel, area.left + 6, py - 2);
        });

        // Zone separators — thicker and darker than the product lines.
        ctx.strokeStyle = "rgba(90,90,90,0.6)";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        [xPx[1], xPx[2]].forEach((px) => {
          ctx.beginPath();
          ctx.moveTo(px, area.top);
          ctx.lineTo(px, area.bottom);
          ctx.stroke();
        });
        [yPx[1], yPx[2]].forEach((py) => {
          ctx.beginPath();
          ctx.moveTo(area.left, py);
          ctx.lineTo(area.right, py);
          ctx.stroke();
        });
        ctx.setLineDash([]);
        ctx.lineWidth = 1;

        // X zone banners (top)
        ctx.font = "bold 12px sans-serif";
        ctx.textBaseline = "middle";
        ctx.textAlign = "center";
        const bannerH = 20;
        for (let xi = 0; xi < 3; xi++) {
          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.fillRect(xPx[xi], area.top, xPx[xi + 1] - xPx[xi], bannerH);
          ctx.fillStyle = "rgba(50,50,50,0.9)";
          ctx.fillText(
            self.zoneLabels[xi],
            (xPx[xi] + xPx[xi + 1]) / 2,
            area.top + bannerH / 2,
          );
        }

        // Y zone banners (right)
        ctx.textAlign = "left";
        const bannerW = 58;
        for (let yi = 0; yi < 3; yi++) {
          const rowTop = yPx[yi + 1];
          const rowH = yPx[yi] - yPx[yi + 1];
          ctx.fillStyle = "rgba(255,255,255,0.55)";
          ctx.fillRect(area.right - bannerW, rowTop, bannerW, rowH);
          ctx.fillStyle = "rgba(50,50,50,0.9)";
          ctx.fillText(
            self.zoneLabels[yi],
            area.right - bannerW + 6,
            (yPx[yi] + yPx[yi + 1]) / 2,
          );
        }

        ctx.restore();
      },
    };
  }

  private buildOptions(yMin: number, yMax: number): ChartOptions {
    const self = this;

    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        xAxes: [
          {
            scaleLabel: {
              display: !!this.data.xAxisLabel,
              labelString: this.data.xAxisLabel || "",
            },
            ticks: {
              min: -5,
              max: 105,
              callback: () => "",
            },
            gridLines: { drawBorder: true, display: false },
          },
        ],
        yAxes: [
          {
            scaleLabel: {
              display: !!this.data.yAxisLabel,
              labelString: this.data.yAxisLabel || "",
            },
            // Place a tick at each product row. Chart.js 2 takes the returned array, but the
            // scale is also mutated in place like the Faculty implementation does, so the
            // behaviour is identical on both sides.
            afterBuildTicks: (scale: any) => {
              const ticks = self.productDisplayY.slice();
              if (scale) {
                scale.ticks = ticks;
              }
              return ticks;
            },
            ticks: {
              min: yMin,
              max: yMax,
              callback: (value: number) => {
                for (let i = 0; i < self.productDisplayY.length; i++) {
                  if (Math.abs(self.productDisplayY[i] - value) < 1e-9) {
                    return self.productLabels[i] || "";
                  }
                }
                return "";
              },
            },
            gridLines: { display: false },
          },
        ],
      },
      tooltips: {
        callbacks: {
          label: (tooltipItem: any, data: any) => {
            const yValue = tooltipItem.yLabel;
            let closestIdx = 0;
            let closestDist = Infinity;
            for (let i = 0; i < self.productDisplayY.length; i++) {
              const distance = Math.abs(self.productDisplayY[i] - yValue);
              if (distance < closestDist) {
                closestDist = distance;
                closestIdx = i;
              }
            }
            const productName = (self.productLabels[closestIdx] || "")
              .replace(/\s*\(.*?\)\s*$/, "")
              .trim();
            const measureName = self.productMeasureLabels[closestIdx] || "";
            const point =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            const formatted = point && point.xRawLabel ? point.xRawLabel : "-";
            return `${productName}  |  ${measureName}: ${formatted}`;
          },
        },
      },
    } as ChartOptions;
  }
}
